import { AccountStatus } from '@capital-markets-gateway/api-client-identity';
import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { numericUtil } from '@cmg/common';
import { DotStatus, Highlights, HighlightsTextItem } from '@cmg/design-system';
import type { VFC } from 'react';
import { Fragment, useMemo } from 'react';

import type { Account } from '../../../../types/domain/account/account';
import {
  accountStatusLabels,
  accountTypeLabels,
  licenseTypeLabels,
} from '../../../../types/domain/account/constants';
import { getSsoStatusText } from './AccountDetailDomainHighlights.model';

export type Props = {
  account: Readonly<Account>;
};

export const AccountDetailDomainHighlights: VFC<Props> = ({ account }) => {
  const canReadGlobalAccount = useCheckPermissions([permissionsByEntity.GlobalAccount.READ]);

  const isAccountActive = account.status === AccountStatus.ACTIVE;

  const licenseCount = useMemo(() => {
    return `${numericUtil.getDisplayValueForInteger(
      account.remainingLicenseCount
    )} of ${numericUtil.getDisplayValueForInteger(account.licenseCount)}`;
  }, [account.licenseCount, account.remainingLicenseCount]);

  return (
    <Highlights
      title={account.name}
      titleItems={
        <Fragment>
          {account.status && (
            <DotStatus
              label={accountStatusLabels[account.status]}
              color={isAccountActive ? 'success' : 'error'}
            />
          )}
          <DotStatus
            label={getSsoStatusText(account.ssoEnabled, account.requireTwoFactorAuth)}
            color={account.ssoEnabled || account.requireTwoFactorAuth ? 'success' : 'error'}
          />
        </Fragment>
      }
      items={
        canReadGlobalAccount ? (
          <Fragment>
            <HighlightsTextItem name="Subdomain" text={account.subdomain} />
            <HighlightsTextItem
              name="Account Type"
              text={account.accountType ? accountTypeLabels[account.accountType] : ''}
            />
            <HighlightsTextItem name="License Type" text={licenseTypeLabels[account.licenseType]} />
            <HighlightsTextItem name="License Count" text={licenseCount} />
          </Fragment>
        ) : undefined
      }
    />
  );
};
